export interface SavedTopic {
  value: string;
  deleted?: boolean;
}

const TOPIC_SPACE_RE = /\s+/g;

function topicKey(value: unknown): string {
  return normalizeTopicValue(value).toLocaleLowerCase();
}

export function normalizeTopicValue(value: unknown): string {
  return typeof value === 'string' ? value.trim().replace(TOPIC_SPACE_RE, ' ') : '';
}

export function isSameTopicValue(a: unknown, b: unknown): boolean {
  return topicKey(a) === topicKey(b);
}

export function normalizeSavedTopics(raw: unknown): SavedTopic[] {
  if (!Array.isArray(raw)) return [];

  const normalized: SavedTopic[] = [];

  for (const item of raw) {
    const value = normalizeTopicValue(
      typeof item === 'string'
        ? item
        : item && typeof item === 'object'
          ? (item as { value?: unknown }).value
          : '',
    );

    if (!value) continue;

    const deleted = Boolean(
      item && typeof item === 'object' && 'deleted' in item && (item as { deleted?: unknown }).deleted,
    );

    const existingIndex = normalized.findIndex((topic) => isSameTopicValue(topic.value, value));
    if (existingIndex >= 0) {
      if (normalized[existingIndex].deleted && !deleted) {
        normalized[existingIndex] = { value };
      }
      continue;
    }

    normalized.push(deleted ? { value, deleted: true } : { value });
  }

  return normalized;
}

export function getActiveTopicValues(topics: SavedTopic[]): string[] {
  return normalizeSavedTopics(topics)
    .filter((topic) => !topic.deleted)
    .map((topic) => topic.value);
}

export function hasActiveSavedTopic(topics: SavedTopic[], value: unknown): boolean {
  const key = topicKey(value);
  if (!key) return false;
  return normalizeSavedTopics(topics).some((topic) => !topic.deleted && topicKey(topic.value) === key);
}

export function upsertSavedTopic(topics: SavedTopic[], value: unknown): SavedTopic[] {
  const normalizedValue = normalizeTopicValue(value);
  const existing = normalizeSavedTopics(topics).filter((topic) => !isSameTopicValue(topic.value, normalizedValue));
  if (!normalizedValue) return existing;
  return [{ value: normalizedValue }, ...existing];
}

export function softDeleteSavedTopic(topics: SavedTopic[], value: unknown): SavedTopic[] {
  const key = topicKey(value);
  if (!key) return normalizeSavedTopics(topics);

  return normalizeSavedTopics(topics).map((topic) =>
    topicKey(topic.value) === key ? { ...topic, deleted: true } : topic,
  );
}
